import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  Zap,
  BookOpen,
  ChevronRight,
  Play,
  Users,
  Lock,
  Sparkles,
  RefreshCw,
  Check,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { Button, Card, Modal } from '../../components/ui';
import { AppLayout } from '../../components/AppLayout';
import { useAuth } from '../../context';
import {
  projectsApi,
  ProjectDto,
  educationalContentApi,
  BlockBreaker as ApiBlockBreaker,
  EducationalArticle as ApiEducationalArticle,
  CreativeChallenge as ApiCreativeChallenge
} from '../../services';
import './EmergencyKit.css';

// Types for UI (mapped from API types)
interface BlockBreaker {
  id: string;
  title: string;
  description: string;
  category: 'physical' | 'mental' | 'social' | 'environmental' | 'creative';
  estimatedMinutes: number;
  icon: string;
}

interface Challenge {
  id: string;
  title: string;
  description: string;
  duration: string;
  difficulty: 'easy' | 'medium' | 'hard';
  participantCount: number;
  isPremium: boolean;
}

interface EducationalContent {
  id: string;
  title: string;
  summary: string;
  category: 'science' | 'inspiration' | 'practical';
  readTime: number;
}

// Transform API types to UI types
const transformBlockBreaker = (api: ApiBlockBreaker): BlockBreaker => ({
  id: api.id,
  title: api.title,
  description: api.description,
  category: api.category as BlockBreaker['category'],
  estimatedMinutes: api.estimatedMinutes,
  icon: api.icon
});

const transformChallenge = (api: ApiCreativeChallenge): Challenge => ({
  id: api.id,
  title: api.title,
  description: api.description,
  duration: api.duration,
  difficulty: api.difficulty as Challenge['difficulty'],
  participantCount: api.participantCount,
  isPremium: api.isPremium
});

const transformArticle = (api: ApiEducationalArticle): EducationalContent => ({
  id: api.id,
  title: api.title,
  summary: api.summary,
  category: api.category as EducationalContent['category'],
  readTime: api.readTimeMinutes
});

type ActiveTab = 'suggestions' | 'five-minutes' | 'challenges' | 'learn';

export const EmergencyKit: React.FC = () => {
  useAuth();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState<ActiveTab>('suggestions');
  const [displayedBreakers, setDisplayedBreakers] = useState<BlockBreaker[]>([]);
  const [allBreakers, setAllBreakers] = useState<BlockBreaker[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [educationalContent, setEducationalContent] = useState<EducationalContent[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectDto | null>(null);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoadingBreakers, setIsLoadingBreakers] = useState(true);
  const [isLoadingChallenges, setIsLoadingChallenges] = useState(true);
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);

  // 5-minute timer state
  const [timerActive, setTimerActive] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(300); // 5 minutes
  const [timerCompleted, setTimerCompleted] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Breathing exercise state
  const [showBreathing, setShowBreathing] = useState(false);
  const [breathPhase, setBreathPhase] = useState<'inhale' | 'hold' | 'exhale'>('inhale');
  const [breathCount, setBreathCount] = useState(0);

  // Fetch block breakers
  const fetchBreakers = useCallback(async () => {
    try {
      setIsLoadingBreakers(true);
      const response = await educationalContentApi.getBlockBreakers(undefined, 10);
      const transformed = response.breakers.map(transformBlockBreaker);
      setAllBreakers(transformed);
      // Show first 4
      setDisplayedBreakers(transformed.slice(0, 4));
    } catch (err) {
      console.error('Error fetching block breakers:', err);
    } finally {
      setIsLoadingBreakers(false);
    }
  }, []);

  // Fetch challenges
  const fetchChallenges = useCallback(async () => {
    try {
      setIsLoadingChallenges(true);
      const response = await educationalContentApi.getChallenges();
      setChallenges(response.challenges.map(transformChallenge));
    } catch (err) {
      console.error('Error fetching challenges:', err);
    } finally {
      setIsLoadingChallenges(false);
    }
  }, []);

  // Fetch educational articles
  const fetchArticles = useCallback(async () => {
    try {
      setIsLoadingArticles(true);
      const response = await educationalContentApi.getArticles();
      setEducationalContent(response.articles.map(transformArticle));
    } catch (err) {
      console.error('Error fetching articles:', err);
    } finally {
      setIsLoadingArticles(false);
    }
  }, []);

  // Fetch data on mount
  useEffect(() => {
    fetchBreakers();
    fetchChallenges();
    fetchArticles();
  }, [fetchBreakers, fetchChallenges, fetchArticles]);

  // Fetch projects for 5-minute timer
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projectList = await projectsApi.getProjects();
        setProjects(projectList);
        if (projectList.length > 0) {
          setSelectedProject(projectList[0]);
        }
      } catch (err) {
        console.error('Error fetching projects:', err);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // Timer logic
  useEffect(() => {
    if (timerActive && timerSeconds > 0) {
      timerRef.current = setInterval(() => {
        setTimerSeconds(prev => {
          if (prev <= 1) {
            setTimerActive(false);
            setTimerCompleted(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerActive]);

  // Breathing exercise logic
  useEffect(() => {
    if (!showBreathing) return;

    const phases: Array<{ phase: 'inhale' | 'hold' | 'exhale'; duration: number }> = [
      { phase: 'inhale', duration: 4000 },
      { phase: 'hold', duration: 4000 },
      { phase: 'exhale', duration: 4000 },
    ];

    let currentIndex = 0;
    setBreathPhase(phases[0].phase);

    const runPhase = () => {
      if (!showBreathing) return;

      currentIndex = (currentIndex + 1) % phases.length;
      setBreathPhase(phases[currentIndex].phase);

      if (currentIndex === 0) {
        setBreathCount(prev => {
          if (prev >= 3) {
            setShowBreathing(false);
            return 0;
          }
          return prev + 1;
        });
      }
    };

    const interval = setInterval(runPhase, 4000);
    return () => clearInterval(interval);
  }, [showBreathing]);

  const shuffleBreakers = useCallback(async () => {
    // If we have local breakers, shuffle them
    if (allBreakers.length > 0) {
      const shuffled = [...allBreakers].sort(() => Math.random() - 0.5);
      setDisplayedBreakers(shuffled.slice(0, 4));
    } else {
      // Otherwise fetch fresh from API
      try {
        const response = await educationalContentApi.getBlockBreakers(undefined, 4);
        setDisplayedBreakers(response.breakers.map(transformBlockBreaker));
      } catch (err) {
        console.error('Error shuffling breakers:', err);
      }
    }
  }, [allBreakers]);

  const formatTimerTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startFiveMinutes = () => {
    setTimerSeconds(300);
    setTimerActive(true);
    setTimerCompleted(false);
  };

  const handleKeepGoing = () => {
    // Navigate to timer with project
    if (selectedProject) {
      navigate(`/timer?project=${selectedProject.id}`);
    } else {
      navigate('/timer');
    }
  };

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      physical: 'coral',
      mental: 'purple',
      social: 'cyan',
      environmental: 'teal',
      creative: 'yellow',
    };
    return colors[category] || 'gray';
  };

  return (
    <AppLayout pageTitle="Emergency Kit">
    <div className="emergency-kit">
      {/* Calming ambient background */}
      <div className="emergency-kit__ambient">
        <div className="emergency-kit__ambient-shape emergency-kit__ambient-shape--1" />
        <div className="emergency-kit__ambient-shape emergency-kit__ambient-shape--2" />
        <div className="emergency-kit__ambient-shape emergency-kit__ambient-shape--3" />
      </div>

      {/* Welcome message */}
      <div className="emergency-kit__welcome">
        <span className="emergency-kit__welcome-icon">🫂</span>
        <h2>Creative blocks are normal.</h2>
        <p>You're not broken. Let's work through this together.</p>
      </div>

      {/* Tab navigation */}
      <div className="emergency-kit__tabs">
        <button
          className={`emergency-kit__tab ${activeTab === 'suggestions' ? 'emergency-kit__tab--active' : ''}`}
          onClick={() => setActiveTab('suggestions')}
        >
          <Sparkles size={18} />
          <span>Suggestions</span>
        </button>
        <button
          className={`emergency-kit__tab ${activeTab === 'five-minutes' ? 'emergency-kit__tab--active' : ''}`}
          onClick={() => setActiveTab('five-minutes')}
        >
          <Clock size={18} />
          <span>5 Minutes</span>
        </button>
        <button
          className={`emergency-kit__tab ${activeTab === 'challenges' ? 'emergency-kit__tab--active' : ''}`}
          onClick={() => setActiveTab('challenges')}
        >
          <Zap size={18} />
          <span>Challenges</span>
        </button>
        <button
          className={`emergency-kit__tab ${activeTab === 'learn' ? 'emergency-kit__tab--active' : ''}`}
          onClick={() => setActiveTab('learn')}
        >
          <BookOpen size={18} />
          <span>Learn</span>
        </button>
      </div>

      {/* Main Content */}
      <main className="emergency-kit__main">
        {/* Suggestions Tab */}
        {activeTab === 'suggestions' && (
          <div className="emergency-kit__suggestions">
            <div className="emergency-kit__section-header">
              <h3>Try one of these</h3>
              <button className="emergency-kit__refresh" onClick={shuffleBreakers} disabled={isLoadingBreakers}>
                <RefreshCw size={16} className={isLoadingBreakers ? 'emergency-kit__spinning' : ''} />
                Shuffle
              </button>
            </div>

            <div className="emergency-kit__breakers">
              {isLoadingBreakers ? (
                <div className="emergency-kit__loading-state">
                  <Loader2 size={24} className="emergency-kit__spinning" />
                  <p>Loading suggestions...</p>
                </div>
              ) : displayedBreakers.map((breaker, index) => (
                <Card
                  key={breaker.id}
                  variant="default"
                  padding="md"
                  className="emergency-kit__breaker-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="emergency-kit__breaker-icon">{breaker.icon}</div>
                  <div className="emergency-kit__breaker-content">
                    <h4>{breaker.title}</h4>
                    <p>{breaker.description}</p>
                    <div className="emergency-kit__breaker-meta">
                      <span className={`emergency-kit__breaker-category emergency-kit__breaker-category--${getCategoryColor(breaker.category)}`}>
                        {breaker.category}
                      </span>
                      <span className="emergency-kit__breaker-time">
                        <Clock size={12} />
                        {breaker.estimatedMinutes} min
                      </span>
                    </div>
                  </div>
                  <button className="emergency-kit__breaker-try">
                    <Check size={18} />
                  </button>
                </Card>
              ))}
            </div>

            {/* Quick breathing button */}
            <button
              className="emergency-kit__breathing-btn"
              onClick={() => {
                setBreathCount(0);
                setBreathPhase('inhale');
                setShowBreathing(true);
              }}
            >
              <span className="emergency-kit__breathing-btn-icon">🌬️</span>
              <span>Take 3 deep breaths</span>
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        {/* 5 Minutes Tab */}
        {activeTab === 'five-minutes' && (
          <div className="emergency-kit__five-minutes">
            {!timerActive && !timerCompleted ? (
              <>
                <div className="emergency-kit__five-intro">
                  <span className="emergency-kit__five-icon">⏱️</span>
                  <h3>Just 5 minutes</h3>
                  <p>Open your project for just 5 minutes. No pressure to do anything great.</p>
                </div>

                <Card variant="elevated" padding="lg" className="emergency-kit__project-select">
                  <label>Choose a work to open:</label>
                  {isLoadingProjects ? (
                    <p className="emergency-kit__loading">Loading...</p>
                  ) : projects.length > 0 ? (
                    <div className="emergency-kit__project-list">
                      {projects.slice(0, 5).map(project => (
                        <button
                          key={project.id}
                          className={`emergency-kit__project-item ${selectedProject?.id === project.id ? 'emergency-kit__project-item--selected' : ''}`}
                          onClick={() => setSelectedProject(project)}
                        >
                          <span>{project.title}</span>
                          {selectedProject?.id === project.id && <Check size={16} />}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="emergency-kit__no-projects">No projects yet</p>
                  )}
                </Card>

                <Button
                  size="lg"
                  className="emergency-kit__start-btn"
                  onClick={startFiveMinutes}
                  disabled={!selectedProject}
                >
                  <Play size={24} />
                  Start 5 Minutes
                </Button>
              </>
            ) : timerActive ? (
              <div className="emergency-kit__timer-active">
                <p className="emergency-kit__timer-project">{selectedProject?.title}</p>
                <div className="emergency-kit__timer-display">
                  <span className="emergency-kit__timer-time">{formatTimerTime(timerSeconds)}</span>
                </div>
                <p className="emergency-kit__timer-message">
                  Just be present. No expectations.
                </p>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setTimerActive(false);
                    setTimerSeconds(300);
                  }}
                >
                  Stop early
                </Button>
              </div>
            ) : (
              <div className="emergency-kit__timer-complete">
                <span className="emergency-kit__complete-icon">🎉</span>
                <h3>You did it!</h3>
                <p>5 minutes is all it takes to start.</p>
                <div className="emergency-kit__complete-actions">
                  <Button variant="ghost" onClick={() => setTimerCompleted(false)}>
                    I'm done
                  </Button>
                  <Button onClick={handleKeepGoing}>
                    Keep going <ArrowRight size={18} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Challenges Tab */}
        {activeTab === 'challenges' && (
          <div className="emergency-kit__challenges">
            <div className="emergency-kit__section-header">
              <h3>Creative Challenges</h3>
            </div>

            <div className="emergency-kit__challenge-list">
              {isLoadingChallenges ? (
                <div className="emergency-kit__loading-state">
                  <Loader2 size={24} className="emergency-kit__spinning" />
                  <p>Loading challenges...</p>
                </div>
              ) : challenges.map((challenge, index) => (
                <Card
                  key={challenge.id}
                  variant="default"
                  padding="md"
                  className="emergency-kit__challenge-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="emergency-kit__challenge-content">
                    <h4>{challenge.title}</h4>
                    <p>{challenge.description}</p>
                    <div className="emergency-kit__challenge-meta">
                      <span className="emergency-kit__challenge-duration">
                        <Clock size={12} />
                        {challenge.duration}
                      </span>
                      <span className={`emergency-kit__challenge-difficulty emergency-kit__challenge-difficulty--${challenge.difficulty}`}>
                        {challenge.difficulty}
                      </span>
                      <span className="emergency-kit__challenge-participants">
                        <Users size={12} />
                        {challenge.participantCount} artists
                      </span>
                    </div>
                  </div>
                  <button className="emergency-kit__challenge-join">
                    <Lock size={14} />
                    Premium
                  </button>
                </Card>
              ))}
            </div>

            <div className="emergency-kit__premium-cta">
              <p>Join challenges and connect with other artists</p>
              <Button variant="secondary">
                Upgrade to Premium
              </Button>
            </div>
          </div>
        )}

        {/* Learn Tab */}
        {activeTab === 'learn' && (
          <div className="emergency-kit__learn">
            <div className="emergency-kit__section-header">
              <h3>Understanding Creative Blocks</h3>
            </div>

            <div className="emergency-kit__articles">
              {isLoadingArticles ? (
                <div className="emergency-kit__loading-state">
                  <Loader2 size={24} className="emergency-kit__spinning" />
                  <p>Loading articles...</p>
                </div>
              ) : educationalContent.map((content, index) => (
                <Card
                  key={content.id}
                  variant="default"
                  padding="md"
                  className="emergency-kit__article-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="emergency-kit__article-content">
                    <span className={`emergency-kit__article-category emergency-kit__article-category--${content.category}`}>
                      {content.category}
                    </span>
                    <h4>{content.title}</h4>
                    <p>{content.summary}</p>
                    <span className="emergency-kit__article-time">
                      <Clock size={12} />
                      {content.readTime} min read
                    </span>
                  </div>
                  <ChevronRight size={20} className="emergency-kit__article-arrow" />
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Breathing Exercise Modal */}
      <Modal
        isOpen={showBreathing}
        onClose={() => setShowBreathing(false)}
        showCloseButton={false}
        closeOnOverlayClick={true}
        size="sm"
      >
        <div className="emergency-kit__breathing">
          <div className={`emergency-kit__breathing-circle emergency-kit__breathing-circle--${breathPhase}`}>
            <span className="emergency-kit__breathing-text">
              {breathPhase === 'inhale' && 'Breathe in...'}
              {breathPhase === 'hold' && 'Hold...'}
              {breathPhase === 'exhale' && 'Breathe out...'}
            </span>
          </div>
          <p className="emergency-kit__breathing-count">
            Breath {breathCount + 1} of 4
          </p>
          <Button variant="ghost" onClick={() => setShowBreathing(false)}>
            Skip
          </Button>
        </div>
      </Modal>

    </div>
    </AppLayout>
  );
};

export default EmergencyKit;
